/**
 * AudioPlayer - Inline audio player for TTS playback
 */

import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { cn } from '@/utils/cn';

interface AudioPlayerProps {
  audioUrl: string;
  className?: string;
  label?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ 
  audioUrl, 
  className,
  label = 'Script Audio'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };

    const setAudioTime = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-1.5 bg-black/30 rounded-lg border border-purple-500/20",
      className
    )}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <button
        onClick={togglePlayPause}
        className="p-1.5 rounded-full bg-purple-500/20 hover:bg-purple-500/30 transition-all group"
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <Pause className="w-3.5 h-3.5 text-purple-400 group-hover:text-purple-300" />
        ) : (
          <Play className="w-3.5 h-3.5 text-purple-400 group-hover:text-purple-300" />
        )}
      </button>

      <div className="flex items-center gap-2">
        <Volume2 className="w-3.5 h-3.5 text-purple-400/60" />
        <span className="text-xs text-white/50 font-mono whitespace-nowrap">
          {label}
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative w-24 h-1 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="absolute left-0 top-0 h-full bg-purple-400/60 transition-all duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>

      <span className="text-xs text-white/40 font-mono">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
    </div>
  );
};

export default AudioPlayer;